import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const CARD: React.CSSProperties = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "14px 18px" };

export function Templates() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const templates = useQuery({ queryKey: ["templates"], queryFn: api.templates });
  const clients = useQuery({ queryKey: ["tenant-clients"], queryFn: api.tenantClients });

  const [startId, setStartId] = useState<string | null>(null);
  const [clientId, setClientId] = useState("");
  const [projName, setProjName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newTasks, setNewTasks] = useState("");

  // Parse the textarea: one task per line; "List: title" groups into a list,
  // a bare line goes under "Tasks". We split only on a "colon-space" preceded by
  // a short single-segment label — so "12:30 standup" (no space after the colon)
  // and pasted URLs ("https://…") stay plain titles instead of being mangled into
  // bogus "12" / "https" lists.
  const LABEL_MAX = 40;
  const parseTasks = (raw: string): { list: string; title: string }[] =>
    raw
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const i = line.indexOf(": ");
        if (i > 0 && i <= LABEL_MAX) {
          const list = line.slice(0, i).trim();
          const title = line.slice(i + 2).trim();
          if (list && title) return { list, title };
        }
        return { list: "Tasks", title: line };
      })
      .filter((t) => t.title);

  const create = useMutation({
    mutationFn: (t: { name: string; tasks: { list: string; title: string }[] }) => api.createTemplate(t.name, t.tasks),
    onSuccess: () => {
      setCreating(false);
      setNewName("");
      setNewTasks("");
      qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });
  const apply = useMutation({
    mutationFn: (t: { id: string; clientId: string; name: string }) => api.applyTemplate(t.id, t.clientId, t.name),
    onSuccess: (r) => navigate({ to: "/projects/$projectId", params: { projectId: r.projectId } }),
  });

  const MAX_TASKS = 300; // must match the API's MAX_TASKS
  const newTaskCount = parseTasks(newTasks).length;
  const tooMany = newTaskCount > MAX_TASKS;

  if (templates.isLoading || clients.isLoading) return <Loading />;
  if (templates.error) return <ErrorNote error={templates.error} />;
  const list = templates.data ?? [];
  const tenantClients = clients.data ?? [];

  return (
    <Page>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Task templates</h1>
          <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>Reusable task lists — start a new client project from one.</div>
        </div>
        <button
          onClick={() => setCreating((v) => !v)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "#fff", background: "#006747", border: 0, borderRadius: 7, padding: "8px 12px", cursor: "pointer" }}
        >
          <Icon name="plus" size={14} color="#fff" /> New template
        </button>
      </div>

      {creating && (
        <div style={CARD}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>New template</div>
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Template name (e.g. New client launch)" style={{ width: "100%", boxSizing: "border-box", fontSize: 13, padding: "7px 10px", border: "1px solid #e2e0d8", borderRadius: 7, marginBottom: 8 }} />
          <textarea
            value={newTasks}
            onChange={(e) => setNewTasks(e.target.value)}
            rows={6}
            placeholder={"One task per line. Group into lists with 'List: task', e.g.\nKickoff: Schedule kickoff call\nKickoff: Collect data samples\nGo-live: Production cutover"}
            style={{ width: "100%", boxSizing: "border-box", fontSize: 12.5, fontFamily: "inherit", padding: "8px 10px", border: "1px solid #e2e0d8", borderRadius: 7 }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <button
              onClick={() => create.mutate({ name: newName.trim(), tasks: parseTasks(newTasks) })}
              disabled={!newName.trim() || tooMany || create.isPending}
              style={{ fontSize: 12.5, fontWeight: 600, color: "#fff", background: newName.trim() && !tooMany ? "#006747" : "#b7c3bd", border: 0, borderRadius: 7, padding: "7px 12px", cursor: newName.trim() && !tooMany ? "pointer" : "default" }}
            >
              {create.isPending ? "Saving…" : "Save template"}
            </button>
            <button onClick={() => setCreating(false)} style={{ fontSize: 12.5, fontWeight: 600, color: "#8a8e88", background: "none", border: 0, cursor: "pointer" }}>Cancel</button>
            <span style={{ fontSize: 11.5, color: tooMany ? "#b3261e" : "#8a8e88" }}>
              {newTaskCount} task{newTaskCount === 1 ? "" : "s"}{tooMany ? ` · too many (max ${MAX_TASKS})` : ""}
            </span>
          </div>
          {create.isError && <div style={{ fontSize: 12, color: "#b3261e", marginTop: 6 }}>Couldn't save — {create.error instanceof Error ? create.error.message : String(create.error)}</div>}
        </div>
      )}

      {apply.isError && (
        <div style={{ ...CARD, background: "#fbe9e7", boxShadow: "inset 0 0 0 1px #f3d3ce", color: "#b3261e", fontSize: 12.5 }}>
          Couldn't start the project — {apply.error instanceof Error ? apply.error.message : String(apply.error)}
        </div>
      )}

      {list.length === 0 ? (
        <div style={{ ...CARD, color: "#8a8e88", fontSize: 13 }}>No templates yet. Create one to capture a reusable task list (e.g. "New client launch").</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 12 }}>
          {list.map((t) => (
            <div key={t.id} style={CARD}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Icon name="folder-kanban" size={16} color="#006747" />
                <span style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</span>
              </div>
              <div style={{ fontSize: 11.5, color: "#8a8e88", marginTop: 6 }}>
                {t.taskCount} task{t.taskCount === 1 ? "" : "s"}{t.lists.length ? ` · ${t.lists.join(", ")}` : ""}
              </div>
              {startId === t.id ? (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                  {clients.error ? (
                    <div style={{ fontSize: 11.5, color: "#b3261e" }}>Couldn't load the client list — reload to try again.</div>
                  ) : (
                    <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={{ fontSize: 12.5, padding: "6px 8px", border: "1px solid #e2e0d8", borderRadius: 6 }}>
                      <option value="">Choose a client…</option>
                      {tenantClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                  <input value={projName} onChange={(e) => setProjName(e.target.value)} placeholder="Project name" style={{ fontSize: 12.5, padding: "6px 8px", border: "1px solid #e2e0d8", borderRadius: 6 }} />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => apply.mutate({ id: t.id, clientId, name: projName.trim() })}
                      disabled={!clientId || !projName.trim() || apply.isPending}
                      style={{ fontSize: 12, fontWeight: 600, color: "#fff", background: clientId && projName.trim() ? "#006747" : "#b7c3bd", border: 0, borderRadius: 6, padding: "6px 10px", cursor: clientId && projName.trim() ? "pointer" : "default" }}
                    >
                      {apply.isPending ? "Creating…" : "Create project"}
                    </button>
                    <button onClick={() => setStartId(null)} style={{ fontSize: 12, fontWeight: 600, color: "#8a8e88", background: "none", border: 0, cursor: "pointer" }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => { setStartId(t.id); setClientId(""); setProjName(t.name); }}
                  style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#006747", background: "#fff", border: "1px solid #cfe0d6", borderRadius: 7, padding: "6px 11px", cursor: "pointer" }}
                >
                  <Icon name="plus" size={13} color="#006747" /> Start a project
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}
